import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, Grid, Typography, Box, Divider, Chip,
    AppBar, Toolbar, IconButton, makeStyles,
    TextField, RadioGroup, Radio, FormControlLabel,
    Paper, Collapse, Switch, MenuItem, Checkbox,
} from '@material-ui/core';
import CloseIcon         from '@material-ui/icons/Close';
import ContactMailIcon   from '@material-ui/icons/ContactMail';
import CreditCardIcon    from '@material-ui/icons/CreditCard';
import AssignmentIcon    from '@material-ui/icons/Assignment';
import HomeIcon          from '@material-ui/icons/Home';
import BusinessIcon      from '@material-ui/icons/Business';
import InfoIcon          from '@material-ui/icons/Info';
import CheckCircleIcon   from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon    from '@material-ui/icons/ExpandMore';
import ExpandLessIcon    from '@material-ui/icons/ExpandLess';
import PlayArrowIcon     from '@material-ui/icons/PlayArrow';
import StarIcon          from '@material-ui/icons/Star';
import CircularProgress  from '@material-ui/core/CircularProgress';
import LoaderWithBackDrop from '../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import {
    DQ_PRO_PLAN_KEY,
    STRIPE_COLLECTION_METHOD,
    COLLECTION_METHOD_LABELS,
    STRIPE_SUBSCRIPTION_STATUS_VERBOSE,
    INSIDER_PROFILE_TYPE_VERBOSE,
    CREDIT_TRANSACTION_TYPE,
} from '../../../../utils/constants';
import { DQProPlans } from '../../../../services/metadata.service';
import { toastSuccess, toastWarning } from '../../../../utils/utils';
import { activateCashSubscriptionService, markInvoicePaidOfflineService } from '../../../../services/organization.service';
import { displayCountry, displayState, findCountry, getCountries, useGeo } from '../../../../utils/geo';
import { ORGANIZATION_TYPE_OPTIONS, PRIMARY_ACTIVITY_OPTIONS } from '../../../../utils/constants';
import { adjustInsiderCredits, grantDqtService, getInsiderDqtLedgerService, getInsiderDqtOrdersService, assignPlanService, getPlanGrantPreviewService } from '../../../../services/billing.service';
import { updateDQInsiderProfileService, setTemplateCreatorFlagService } from '../../../../services/dq_insider.service';
import InsiderProductAccessDialog from '../../../ProductAccess/InsiderAccess/components/InsiderProductAccessDialog.comp';
import InsiderFeaturesDialog from '../../../ProductAccess/InsiderAccess/components/InsiderFeaturesDialog.comp';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const isValidEmail = (email) => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getProfileTypeLabel = (profile) => {
    if (!isValidEmail(profile.email)) return INSIDER_PROFILE_TYPE_VERBOSE.GUEST;
    const planKey = profile.dq_pro_plan?.plan_key;
    if (planKey === 'dq_pro')        return INSIDER_PROFILE_TYPE_VERBOSE.BUSINESS;
    if (planKey === 'educator_plan') return INSIDER_PROFILE_TYPE_VERBOSE.EDUCATOR;
    if (profile.profile_type === 'INDIVIDUAL') return INSIDER_PROFILE_TYPE_VERBOSE.EDUCATOR;
    return INSIDER_PROFILE_TYPE_VERBOSE[profile.profile_type] || profile.profile_type || 'Individual';
};

/* ─── styles ───────────────────────────────────────────────────────────── */

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        backgroundColor: '#f8f9fa',
        padding: theme.spacing(3),
    },
    sectionCard: {
        backgroundColor: '#fff',
        padding: theme.spacing(3),
        borderRadius: theme.spacing(2),
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        marginBottom: theme.spacing(3),
        border: '1px solid #edf2f7',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
        '& svg': { marginRight: theme.spacing(1.5), fontSize: '1.4rem' },
        '& h6':  { fontWeight: 700, letterSpacing: '0.02em' },
    },
    fieldLabel: {
        display: 'block',
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        fontWeight: 600,
        letterSpacing: '0.05em',
    },
    fieldValue: {
        fontWeight: 500,
        wordBreak: 'break-all',
    },
    /* action panel at the bottom of DialogContent */
    actionPanel: {
        backgroundColor: '#fff',
        border: '1px solid #edf2f7',
        borderRadius: theme.spacing(2),
        marginBottom: theme.spacing(1),
        overflow: 'hidden',
    },
    actionPanelRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 2.5),
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { backgroundColor: '#f5f7ff' },
        transition: 'background 0.15s',
    },
    actionPanelBody: {
        padding: theme.spacing(2, 2.5, 2.5),
        borderTop: '1px solid #edf2f7',
        backgroundColor: '#fafbff',
    },
    /* plan radio cards */
    planGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    planCard: {
        padding: theme.spacing(1.25, 1.5),
        borderRadius: theme.spacing(1),
        border: '2px solid #edf2f7',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        '&:hover': { borderColor: theme.palette.primary.light, backgroundColor: '#f0f4ff' },
    },
    planCardSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#ebf0ff',
    },
}));

/* ─── tiny reusables ───────────────────────────────────────────────────── */

// Readable labels for the enquiry bands stored in background_and_experience.
const BG_COUNT_BAND_LABELS = {
    under_100: 'Under 100', '100_500': '100 – 500', '500_1000': '500 – 1,000',
    '1000_5000': '1,000 – 5,000', '5000_10000': '5,000 – 10,000', '10000_plus': '10,000+',
};
const BG_ROLLOUT_LABELS = {
    immediate: 'Immediately', '1_3_months': '1 – 3 months', '3_6_months': '3 – 6 months',
    '6_12_months': '6 – 12 months', '12_plus_months': '12+ months',
};

/**
 * Turn the profile's `additional_details` object (written by the admin "Add
 * Insider" flow) into a flat list of readable { label, value } rows. Returns
 * null when there are no additional details to show.
 */
const parseEnquiryDetails = (bg) => {
    if (!bg || typeof bg !== 'object') return null;

    const scale = bg.scale || {};
    const rows = [
        ['Title', bg.title],
        ['# of children', BG_COUNT_BAND_LABELS[scale.num_children] || scale.num_children],
        ['# of parents', BG_COUNT_BAND_LABELS[scale.num_parents] || scale.num_parents],
        ['# of teachers', BG_COUNT_BAND_LABELS[scale.num_teachers] || scale.num_teachers],
        ['# of schools', BG_COUNT_BAND_LABELS[scale.num_schools] || scale.num_schools],
        ['# of companies', BG_COUNT_BAND_LABELS[scale.num_companies] || scale.num_companies],
        ['Roll-out period', BG_ROLLOUT_LABELS[bg.rollout_period] || bg.rollout_period],
        ['Translations', bg.translations_needed
            ? `${bg.translations_needed}${bg.translations_language ? ` (${bg.translations_language})` : ''}`
            : null],
        ['Notes', bg.message],
    ];
    return rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
};

const optionLabel = (options, code) => (options.find((o) => o.value === code) || {}).label || code;

/** Primary activities as labels. */
const primaryActivitiesLabel = (profile) => {
    const codes = profile.primary_activities || [];
    if (!codes.length) return null;
    return codes.map((c) => optionLabel(PRIMARY_ACTIVITY_OPTIONS, c)).join(', ');
};

const DataField = ({ label, value, sm = 4 }) => {
    const classes = useStyles();
    if (value === null || value === undefined || value === '') return null;
    return (
        <Grid item xs={12} sm={sm}>
            <Box minHeight={48} mb={1}>
                <Typography variant="caption" className={classes.fieldLabel}>{label}</Typography>
                <Typography variant="body1" className={classes.fieldValue}>
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                </Typography>
            </Box>
        </Grid>
    );
};

/* Why a plan's own price cannot be granted — shown under the "How much" select. */
const PLAN_PRICE_BLOCKED = {
    PLAN_HAS_NO_ONETIME_PRICE: 'This plan has no one-time Stripe price yet — set one on the plan, or enter the amount.',
    RATE_NOT_SET: 'No DQ Token rate is set yet, so DQ Tokens cannot be granted.',
    PRECISION_UNDECIDED: 'The plan price is not a whole number of DQ Tokens at the current rate.',
    PRICE_NOT_ONE_TIME: 'The plan points at a recurring Stripe price. Fix it on the plan, or enter the amount.',
    PRICE_NOT_USD: 'The plan price is not in US dollars. Fix it on the plan, or enter the amount.',
    PRICE_INACTIVE: 'The plan price is archived in Stripe. Fix it on the plan, or enter the amount.',
    PRICE_UNAVAILABLE: 'The plan price could not be read from Stripe. Try again, or enter the amount.',
    PRICE_HAS_NO_AMOUNT: 'The plan price has no amount in Stripe. Fix it on the plan, or enter the amount.',
    PRICE_NOT_SET: 'This direct plan has no DQ Token price per credit, so a grant cannot become credits. Set it on the plan.',
};

/* ─── main component ───────────────────────────────────────────────────── */

const PBDQDetailsDialog = ({ open, onClose, loading, profile, onActivated, onRefresh }) => {
    useGeo(); // re-render with the API's country list once it loads
    const classes = useStyles();

    /* plans fetched from API: [{ key, label, is_active }] */
    const [plansList,   setPlansList]   = useState([]);
    const [plansMap,    setPlansMap]    = useState({});

    useEffect(() => {
        if (!open) return;
        DQProPlans.listData()
            .then((res) => {
                const rows = (res && res.data && res.data.metaData) ? res.data.metaData : [];
                const active = rows.filter((p) => p.is_active !== false && p.key && p.key !== DQ_PRO_PLAN_KEY.DEFAULT);
                // Sort by order_no then label for stable display order
                active.sort((a, b) => (a.order_no || 999) - (b.order_no || 999) || (a.label || '').localeCompare(b.label || ''));
                setPlansList(active);
                const map = {};
                rows.forEach((p) => { if (p.key) map[p.key] = p.label || p.key; });
                setPlansMap(map);
            })
            .catch(() => {
                // Silently ignore — plan cards will remain empty
            });
    }, [open]);

    useEffect(() => {
        if (!open) {
            setActivePanel(null);
            setOpenPACDialog(false);
            setOpenFeaturesDialog(false);
        }
    }, [open]);

    const notifyRefresh = () => {
        if (typeof onRefresh === 'function') onRefresh();
    };

    /* panel state: null | 'activate' | 'offline' */
    const [activePanel, setActivePanel]   = useState(null);

    /* account activation ("take action") */
    const [activatingAccount, setActivatingAccount] = useState(false);

    // Activate a pending insider account: marks it active and emails the user
    // their login credentials (admin gets no email).
    const handleActivateAccount = async () => {
        if (!profile?.dq_insider_id) { toastWarning('Missing required data'); return; }
        setActivatingAccount(true);
        try {
            const res = await updateDQInsiderProfileService(profile.dq_insider_id);
            toastSuccess((res && res.message) || 'Account activated. Login details emailed to the user.');
            if (typeof onActivated === 'function') onActivated();
            onClose();
        } catch (err) {
            // invokeApi already shows the error toast — just log (avoids a duplicate toast).
            console.error('[ActivateAccount] failed:', err);
        } finally {
            setActivatingAccount(false);
        }
    };

    /* activate subscription form */
    const [selectedPlan,  setSelectedPlan]  = useState('');
    const [activating,    setActivating]    = useState(false);
    const [isActivated,   setIsActivated]   = useState(false);

    /* offline invoice form */
    const [offlineNote,  setOfflineNote]  = useState('');
    const [markingPaid,  setMarkingPaid]  = useState(false);

    /* adjust credits form */
    const [adjustCreditsAmount, setAdjustCreditsAmount] = useState('');
    const [adjustCreditsReason, setAdjustCreditsReason] = useState('');
    const [adjustingCredits, setAdjustingCredits] = useState(false);

    /* grant DQ Tokens form — DQT against a recorded offline payment. The API
     * turns the USD into DQT at the current rate and refuses a reference it has
     * already granted, a direct-plan workspace, or a missing rate; its message is
     * toasted by invokeApi. */
    const [dqtAmountUsd, setDqtAmountUsd] = useState('');
    const [dqtReference, setDqtReference] = useState('');
    const [dqtNote, setDqtNote] = useState('');
    const [grantingDqt, setGrantingDqt] = useState(false);
    const [dqtInfo, setDqtInfo] = useState(null); // { dqt_balance, entries }
    const [dqtOrders, setDqtOrders] = useState([]);

    const loadDqt = async (dq_insider_id) => {
        try {
            const [res, ord] = await Promise.all([
                getInsiderDqtLedgerService(dq_insider_id, 1, 5),
                getInsiderDqtOrdersService(dq_insider_id, 1, 5),
            ]);
            setDqtInfo(res?.data ? { dqt_balance: res.data.dqt_balance ?? 0, entries: res.data.entries || [] } : null);
            setDqtOrders(ord?.data?.orders || []);
        } catch (err) {
            console.error('[DQT] ledger load failed:', err);
            setDqtInfo(null);
            setDqtOrders([]);
        }
    };

    useEffect(() => {
        if (open && profile?.dq_insider_id) loadDqt(profile.dq_insider_id);
        if (!open) { setDqtInfo(null); setDqtOrders([]); }
    }, [open, profile?.dq_insider_id]); // eslint-disable-line react-hooks/exhaustive-deps

    /* assign plan without a subscription (R28) — optionally with the DQT agreed
     * with the customer (R49). The API refuses a plan change (still open, Q20) and a
     * live subscription; invokeApi toasts its message.
     *
     * A direct plan (23 Sep 2026) makes the workspace a school or a school network:
     * its organisation is created on first assignment, and any grant becomes credits
     * in that organisation's pool — the workspace is never shown DQ Tokens. */
    const [assignPlanKey, setAssignPlanKey] = useState('');
    const [assignAccountType, setAssignAccountType] = useState('school');
    const [assignOrgName, setAssignOrgName] = useState('');
    const [assignCountry, setAssignCountry] = useState('');
    const [assignWithGrant, setAssignWithGrant] = useState(true);
    // 'plan_price' grants the plan's own one-time price — the usual case, and the
    // same DQT the partner would have received buying the plan in the portal.
    const [assignGrantMode, setAssignGrantMode] = useState('plan_price');
    const [assignUsd, setAssignUsd] = useState('');
    const [assignRef, setAssignRef] = useState('');
    const [assigningPlan, setAssigningPlan] = useState(false);
    const [planPreview, setPlanPreview] = useState(null);   // { amount_usd, dqt_amount, can_grant_plan_price, blocked_reason }

    // What the selected plan's price would grant. Read from the API so the figure
    // shown is the Stripe price and the rate in force, never a guess.
    useEffect(() => {
        let cancelled = false;
        if (activePanel !== 'assign_plan' || !assignPlanKey) { setPlanPreview(null); return undefined; }
        getPlanGrantPreviewService(assignPlanKey)
            .then((res) => { if (!cancelled) setPlanPreview(res?.data || null); })
            .catch(() => { if (!cancelled) setPlanPreview(null); });
        return () => { cancelled = true; };
    }, [activePanel, assignPlanKey]);

    /* PAC Dialog form */
    const [openPACDialog, setOpenPACDialog] = useState(false);
    const [openFeaturesDialog, setOpenFeaturesDialog] = useState(false);

    /* Template Creator toggle — grants free authoring of DQ showcase/template
     * learning programs in dq-seal-client. See
     * src/docs/Template-Learning-Programs-Architecture.md. */
    const [templateCreator, setTemplateCreator] = useState(false);
    const [savingTemplateCreator, setSavingTemplateCreator] = useState(false);

    useEffect(() => {
        setTemplateCreator(!!profile?.is_template_creator);
    }, [profile]);

    const handleToggleTemplateCreator = async (e) => {
        const next = e.target.checked;
        if (!profile?.dq_insider_id) { toastWarning('Missing required data'); return; }

        setSavingTemplateCreator(true);
        setTemplateCreator(next);
        try {
            await setTemplateCreatorFlagService(profile.dq_insider_id, next);
            toastSuccess(next ? 'Template-creator access granted.' : 'Template-creator access revoked.');
            notifyRefresh();
        } catch (err) {
            setTemplateCreator(!next);
            toastWarning(err.message || 'Failed to update template-creator status');
        } finally {
            setSavingTemplateCreator(false);
        }
    };

    const togglePanel = (name) => {
        if (activePanel === name) {
            setActivePanel(null);
        } else {
            if (name === 'activate') {
                setSelectedPlan(profile?.dq_pro_plan?.plan_key || DQ_PRO_PLAN_KEY.DEFAULT);
            }
            if (name === 'assign_plan') {
                const current = profile?.dq_pro_plan?.plan_key;
                setAssignPlanKey(current && current !== DQ_PRO_PLAN_KEY.DEFAULT ? current : '');
            }
            setActivePanel(name);
        }
    };

    const handleActivateSubscription = async () => {
        if (!profile?.owner_user_id) { toastWarning('Missing required data'); return; }
        if (selectedPlan === DQ_PRO_PLAN_KEY.DEFAULT) { toastWarning('Please select a plan other than Default'); return; }

        setActivating(true);
        try {
            const payload = {
                owner_user_id: profile.owner_user_id,
                plan_key: selectedPlan,
            };

            console.log(' [ActivateSubscription] Payload being sent:', payload);

            await activateCashSubscriptionService(
                profile.owner_user_id,
                selectedPlan,
            );

            toastSuccess('Subscription activated successfully');
            setIsActivated(true);
            setActivePanel(null);
            notifyRefresh();

        } catch (err) {
            toastWarning(err.message || 'Activation failed');
        } finally {
            setActivating(false);
        }
    };

    const handleMarkInvoicePaidOffline = async () => {
        if (!profile?.dq_insider_id) {
            toastWarning('Missing required invoice data');
            return;
        }

        const payload = {
            dq_insider_id: profile.dq_insider_id,
            note: offlineNote
        };

        console.log('🚀 [OfflineInvoice] Payload being sent:', payload);

        setMarkingPaid(true);

        try {
            await markInvoicePaidOfflineService(
                profile.dq_insider_id,
                offlineNote
            );

            toastSuccess('Invoice marked as paid offline');
            setOfflineNote('');
            setActivePanel(null);
            notifyRefresh();

        } catch (err) {
            console.error('❌ [OfflineInvoice] Error:', err);
            toastWarning(err.message || 'Failed to mark invoice paid');
        } finally {
            setMarkingPaid(false);
        }
    };

    const handleAdjustCredits = async () => {
        if (!profile?.dq_insider_id) { toastWarning('Missing required data'); return; }
        if (!adjustCreditsAmount || isNaN(adjustCreditsAmount)) { toastWarning('Please enter a valid amount'); return; }
        if (!adjustCreditsReason) { toastWarning('Please enter a reason'); return; }

        const payload = {
            dq_insider_id: profile.dq_insider_id,
            amount_credits: Number(adjustCreditsAmount),
            description: adjustCreditsReason,
            transaction_type: CREDIT_TRANSACTION_TYPE.ADJUSTMENT
        };

        console.log('--- SENDING ADJUST CREDITS PAYLOAD ---', payload);

        setAdjustingCredits(true);
        try {
            await adjustInsiderCredits(payload);
            toastSuccess('Credits adjusted successfully');
            setAdjustCreditsAmount('');
            setAdjustCreditsReason('');
            setActivePanel(null);
            notifyRefresh();
        } catch (err) {
            toastWarning(err.message || 'Credit adjustment failed');
        } finally {
            setAdjustingCredits(false);
        }
    };

    const handleGrantDqt = async () => {
        if (!profile?.dq_insider_id) { toastWarning('Missing required data'); return; }
        const amount = Number(dqtAmountUsd);
        if (!dqtAmountUsd || !Number.isFinite(amount) || amount <= 0) { toastWarning('Enter the USD amount received'); return; }
        if (!dqtReference.trim()) { toastWarning('Enter the payment reference'); return; }

        setGrantingDqt(true);
        try {
            const res = await grantDqtService({
                dq_insider_id: profile.dq_insider_id,
                amount_usd: amount,
                payment_reference: dqtReference.trim(),
                description: dqtNote.trim() || undefined,
            });
            const d = res?.data;
            toastSuccess(d
                ? `Granted ${d.amount_dqt} DQT (US$${d.amount_usd} at US$${d.usd_per_dqt} per DQT)${d.invoice_number ? ` · Stripe invoice ${d.invoice_number}, paid outside Stripe` : ''}.`
                : 'DQ Tokens granted');
            if (d && !d.invoice_number) {
                toastWarning(`The DQ Tokens were granted, but the Stripe invoice could not be created${d.invoice_error ? `: ${d.invoice_error}` : ''}.`);
            }
            setDqtAmountUsd('');
            setDqtReference('');
            setDqtNote('');
            setActivePanel(null);
            await loadDqt(profile.dq_insider_id);
            notifyRefresh();
        } catch (err) {
            // invokeApi has already toasted the API's message (duplicate reference, no rate set, …).
            console.error('[GrantDqt] failed:', err);
        } finally {
            setGrantingDqt(false);
        }
    };

    const handleAssignPlan = async () => {
        if (!profile?.dq_insider_id) { toastWarning('Missing required data'); return; }
        if (!assignPlanKey) { toastWarning('Choose a plan'); return; }
        const isDirect = plansList.find((p) => p.key === assignPlanKey)?.channel === 'direct';
        if (isDirect && !assignOrgName.trim()) { toastWarning('Enter the name of the school or school network'); return; }
        if (isDirect && !assignCountry) { toastWarning('Choose the country of the school or school network'); return; }
        let grant;
        if (assignWithGrant) {
            if (!assignRef.trim()) { toastWarning('Enter the payment reference'); return; }
            if (assignGrantMode === 'plan_price') {
                if (planPreview && planPreview.can_grant_plan_price === false) {
                    toastWarning('This plan has no usable one-time price — choose "A different amount"');
                    return;
                }
                // No amount: the API grants the plan's own one-time price.
                grant = { source: 'plan_price', payment_reference: assignRef.trim() };
            } else {
                const amount = Number(assignUsd);
                if (!assignUsd || !Number.isFinite(amount) || amount <= 0) { toastWarning('Enter the USD amount received'); return; }
                grant = { amount_usd: amount, payment_reference: assignRef.trim() };
            }
        }

        setAssigningPlan(true);
        try {
            const res = await assignPlanService({
                dq_insider_id: profile.dq_insider_id,
                plan_key: assignPlanKey,
                grant,
                ...(isDirect ? { account: { account_type: assignAccountType, org_name: assignOrgName.trim(), country: assignCountry } } : {}),
            });
            const d = res?.data;
            const planText = d?.already_active ? `${d.plan_label} was already active` : `${d?.plan_label || assignPlanKey} is now active`;
            if (d?.direct) {
                // A direct workspace: credits only, never DQ Tokens.
                toastSuccess(`${planText}${d.direct.org_created ? ` · ${assignAccountType === 'school_network' ? 'school network' : 'school'} created` : ''}${d.direct.credits ? ` · ${Number(d.direct.credits).toLocaleString('en-US')} credits added` : ''}${d.grant?.invoice_number ? ` · Stripe invoice ${d.grant.invoice_number}` : ''}.`);
                if (d.direct.conversion_error) toastWarning(`The payment was recorded, but it could not be turned into credits yet: ${d.direct.conversion_error}. Assign the plan again to retry.`);
            } else {
                toastSuccess(d?.grant
                    ? `${planText} · granted ${d.grant.amount_dqt} DQT${d.grant.invoice_number ? ` · Stripe invoice ${d.grant.invoice_number}` : ''}.`
                    : `${planText}.`);
                if (d?.grant && !d.grant.invoice_number) {
                    toastWarning(`The DQ Tokens were granted, but the Stripe invoice could not be created${d.grant.invoice_error ? `: ${d.grant.invoice_error}` : ''}.`);
                }
            }
            setAssignUsd('');
            setAssignRef('');
            setAssignWithGrant(true);
            setAssignGrantMode('plan_price');
            setActivePanel(null);
            await loadDqt(profile.dq_insider_id);
            notifyRefresh();
        } catch (err) {
            // invokeApi has already toasted the API's reason.
            console.error('[AssignPlan] failed:', err);
        } finally {
            setAssigningPlan(false);
        }
    };

    // Plan-price hint under the "How much" select: the Stripe price and the DQT it buys.
    const planPriceHint = !planPreview
        ? 'Reading the plan price…'
        : planPreview.can_grant_plan_price
            ? (planPreview.channel === 'direct'
                ? `US${Number(planPreview.amount_usd).toLocaleString('en-US')} → ${Number(planPreview.credits).toLocaleString('en-US')} credits for the school or network`
                : `US${Number(planPreview.amount_usd).toLocaleString('en-US')} → ${Number(planPreview.dqt_amount).toLocaleString('en-US')} DQT at US${planPreview.usd_per_dqt} per DQT`)
            : (PLAN_PRICE_BLOCKED[planPreview.blocked_reason] || 'The plan price cannot be used — enter the amount instead.');

    const isGuest           = !isValidEmail(profile?.email);
    const isDefaultPlan     = profile?.dq_pro_plan?.plan_key === DQ_PRO_PLAN_KEY.DEFAULT;

    /**
     * A plan is live on this workspace: anything but Default, marked active. Covers
     * both routes in — an admin assignment and the older cash subscription.
     */
    const hasActivePlan = !isDefaultPlan
        && !!profile?.dq_pro_plan?.plan_key
        && profile?.dq_pro_plan?.status === 'active';

    /**
     * Activate Subscription is retired. PbDQ sells plans one-time now: the partner
     * buys the plan in the portal, or an admin assigns it below. Creating a Stripe
     * subscription here would put the workspace on the recurring model and block
     * both. The panel is kept, hidden, because the recurring model still exists in
     * the API and may be offered again.
     */
    const SHOW_ACTIVATE_SUBSCRIPTION = false;
    const showActivatePanel = SHOW_ACTIVATE_SUBSCRIPTION && !isGuest && !isActivated && profile?.subscription_status !== 'active';

    // Assign Plan puts a workspace ON a plan. Once one is active there is nothing to
    // assign — changing plans is a separate, still-undecided flow — so the panel goes
    // away and the partner tops up DQ Tokens from the portal instead.
    const showAssignPlanPanel = !hasActivePlan;

    // Only a real Stripe subscription has an invoice to mark paid. A plan assigned
    // by an admin (or bought one-time) has none, so the action would always fail.
    const showOfflinePanel  = !isDefaultPlan
        && !!profile?.stripe_flat_subscription_id
        && profile?.subscription_collection_method !== STRIPE_COLLECTION_METHOD.CHARGE_AUTOMATICALLY;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <AppBar position="static" color="white" elevation={0} style={{ borderBottom: '1px solid #eee' }}>
                <Toolbar style={{ justifyContent: 'space-between' }}>
                    <Typography variant="h6" color="primary">PBDQ Profile Details</Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {profile && !profile.is_active && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleActivateAccount}
                                disabled={activatingAccount}
                                startIcon={activatingAccount ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                style={{ textTransform: 'none' }}
                            >
                                Activate Account
                            </Button>
                        )}
                        <IconButton edge="end" color="inherit" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            <DialogContent className={classes.dialogContent}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={5} flexDirection="column">
                        <LoaderWithBackDrop loading={true} />
                        <Typography style={{ marginTop: 20 }}>Loading profile details...</Typography>
                    </Box>
                ) : profile ? (
                    <Grid container spacing={1}>

                        {/* 1. Identity */}
                        <Grid item xs={12}>
                            <div className={classes.sectionCard}>
                                <div className={classes.sectionHeader}>
                                    <InfoIcon />
                                    <Typography variant="subtitle1">Identity & System Info</Typography>
                                </div>
                                <Grid container spacing={2}>
                                    <DataField label="Insider ID"    value={profile.dq_insider_id} sm={5} />
                                    <DataField label="Email Address" value={isValidEmail(profile.email) ? profile.email : null} sm={4} />
                                    <DataField label="Status"        value={profile.is_active ? 'Active' : 'Inactive'} sm={3} />
                                    <DataField label="Profile Type"  value={getProfileTypeLabel(profile)} sm={4} />
                                </Grid>
                            </div>
                        </Grid>

                        {/* 2. Personal */}
                        {(profile.first_name || profile.last_name || profile.phone_number || profile.website) && (
                            <Grid item xs={12}>
                                <div className={classes.sectionCard}>
                                    <div className={classes.sectionHeader}>
                                        <ContactMailIcon />
                                        <Typography variant="subtitle1">Personal Information</Typography>
                                    </div>
                                    <Grid container spacing={2}>
                                        <DataField label="First Name"    value={profile.first_name}    sm={4} />
                                        <DataField label="Last Name"     value={profile.last_name}     sm={4} />
                                        <DataField label="Phone Number"  value={[profile.phone_country_code, profile.phone_number].filter(Boolean).join(' ')}  sm={4} />
                                        <DataField label="Website"       value={profile.website}       sm={4} />
                                    </Grid>
                                </div>
                            </Grid>
                        )}

                        {/* 3. Location */}
                        {(profile.address || profile.city || profile.state || profile.postal_code || profile.country || profile.target_country) && (
                            <Grid item xs={12}>
                                <div className={classes.sectionCard}>
                                    <div className={classes.sectionHeader}>
                                        <HomeIcon />
                                        <Typography variant="subtitle1">Location & Address</Typography>
                                    </div>
                                    <Grid container spacing={2}>
                                        <DataField label="Address"        value={profile.address}        sm={4} />
                                        <DataField label="City"           value={profile.city}           sm={4} />
                                        <DataField label="State / Region" value={displayState(profile.state)}          sm={4} />
                                        <DataField label="Postal Code"    value={profile.postal_code}    sm={4} />
                                        <DataField label="Country"        value={displayCountry(profile.country)}        sm={4} />
                                        <DataField label="Target Country" value={profile.target_country} sm={4} />
                                    </Grid>
                                </div>
                            </Grid>
                        )}

                        {/* 4. Business */}
                        {(profile.business_name || profile.business_type || (profile.primary_activities || []).length || profile.background_and_experience || profile.additional_details || profile.target_audience || profile.target_promotion_method || profile.referral_code) && (
                            <Grid item xs={12}>
                                <div className={classes.sectionCard}>
                                    <div className={classes.sectionHeader}>
                                        <BusinessIcon />
                                        <Typography variant="subtitle1">Business & Professional</Typography>
                                    </div>
                                    <Grid container spacing={2}>
                                        <DataField label="Business Name"         value={profile.business_name}              sm={4} />
                                        {/* A code from Add Insider (organization type); free text from the old self-registration form. */}
                                        <DataField label="Business Type"         value={profile.business_type && optionLabel(ORGANIZATION_TYPE_OPTIONS, profile.business_type)} sm={4} />
                                        <DataField label="Primary Role / Activity" value={primaryActivitiesLabel(profile)}  sm={8} />
                                        <DataField label="Referral Code"         value={profile.referral_code}              sm={4} />
                                        <DataField label="Target Audience"       value={profile.target_audience}            sm={4} />
                                        <DataField label="Promotion Method"      value={profile.target_promotion_method}    sm={4} />
                                        {/* Free-text background (e.g. self-signups) */}
                                        <DataField label="Background & Exp."     value={profile.background_and_experience}  sm={12} />
                                        {/* Structured additional details (admin "Add Insider" flow) */}
                                        {(parseEnquiryDetails(profile.additional_details) || []).map(([label, value]) => (
                                            <DataField key={label} label={label} value={value} sm={4} />
                                        ))}
                                    </Grid>
                                </div>
                            </Grid>
                        )}

                        {/* 4b. Legal entity (sign-up registry / admin "Add Insider") */}
                        {(profile.registered_legal_name || profile.registration_country || profile.registered_address_line1) && (
                            <Grid item xs={12}>
                                <div className={classes.sectionCard}>
                                    <div className={classes.sectionHeader}>
                                        <AssignmentIcon />
                                        <Typography variant="subtitle1">Legal Entity</Typography>
                                    </div>
                                    <Grid container spacing={2}>
                                        <DataField label="Registered Name"         value={profile.registered_legal_name}     sm={4} />
                                        <DataField label="Country of Registration" value={displayCountry(profile.registration_country)}      sm={4} />
                                        <DataField label="Registered Address"      value={[profile.registered_address_line1, profile.registered_address_line2].filter(Boolean).join(', ')} sm={4} />
                                        <DataField label="City"                    value={profile.registered_city}           sm={4} />
                                        <DataField label="State / Region"          value={displayState(profile.registered_state)}          sm={4} />
                                        <DataField label="Postal Code"             value={profile.registered_postal_code}    sm={4} />
                                    </Grid>
                                </div>
                            </Grid>
                        )}

                        {/* 5. Plan & Subscription */}
                        <Grid item xs={12}>
                            <div className={classes.sectionCard}>
                                <div className={classes.sectionHeader}>
                                    <CreditCardIcon />
                                    <Typography variant="subtitle1">Plan & Subscription Details</Typography>
                                </div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" className={classes.fieldLabel}>Current Plan</Typography>
                                        <div style={{ marginTop: 4 }}>
                                            <Chip
                                                label={plansMap[profile.dq_pro_plan?.plan_key] || profile.dq_pro_plan?.plan_key || 'N/A'}
                                                size="small" color="primary"
                                                style={{ height: 24, fontWeight: 600 }}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" className={classes.fieldLabel}>Plan Status</Typography>
                                        <div style={{ marginTop: 4 }}>
                                            <Chip
                                                label={profile.dq_pro_plan?.status || 'N/A'}
                                                size="small" variant="outlined"
                                                color={profile.dq_pro_plan?.status === 'active' ? 'primary' : 'default'}
                                                style={{ height: 24 }}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" className={classes.fieldLabel}>Plan Selected At</Typography>
                                        <Typography variant="body1" className={classes.fieldValue}>
                                            {profile.dq_pro_plan?.selected_at
                                                ? new Date(profile.dq_pro_plan.selected_at).toLocaleString('en-GB', {
                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit', hour12: true,
                                                })
                                                : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}><Divider style={{ margin: '8px 0' }} /></Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" className={classes.fieldLabel}>Subscription Status</Typography>
                                        <div style={{ marginTop: 4 }}>
                                            <Chip
                                                label={STRIPE_SUBSCRIPTION_STATUS_VERBOSE[profile.subscription_status] || profile.subscription_status || 'N/A'}
                                                size="small"
                                                color={profile.subscription_status === 'active' ? 'primary' : 'secondary'}
                                                style={{ height: 24, textTransform: 'capitalize' }}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" className={classes.fieldLabel}>Payment Collection Method</Typography>
                                        <Typography variant="body1" className={classes.fieldValue}>
                                            {COLLECTION_METHOD_LABELS[profile.subscription_collection_method] || profile.subscription_collection_method || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="caption" className={classes.fieldLabel}>Next Renewal</Typography>
                                        <Typography variant="body1" className={classes.fieldValue}>
                                            {profile.subscription_renews_at
                                                ? new Date(profile.subscription_renews_at).toLocaleString('en-GB', {
                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                })
                                                : 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>

                        {/* 6. Actions — only rendered when at least one action is available */}
                        {(showActivatePanel || showOfflinePanel || true) && (
                            <Grid item xs={12}>
                                <div className={classes.sectionCard} style={{ marginBottom: 0 }}>
                                    <div className={classes.sectionHeader}>
                                        <AssignmentIcon />
                                        <Typography variant="subtitle1">Actions</Typography>
                                    </div>

                                    {/* Enabled panels only — rendered in priority order (activate first, offline second) */}

                                    {showActivatePanel && (
                                        <div className={classes.actionPanel}>
                                            <div className={classes.actionPanelRow} onClick={() => togglePanel('activate')}>
                                                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                    <PlayArrowIcon fontSize="small" color="secondary" />
                                                    <Box>
                                                        <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                            Activate Subscription
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Select a plan and confirm to activate
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {activePanel === 'activate'
                                                    ? <ExpandLessIcon fontSize="small" color="action" />
                                                    : <ExpandMoreIcon fontSize="small" color="action" />}
                                            </div>
                                            <Collapse in={activePanel === 'activate'} timeout={200}>
                                                <div className={classes.actionPanelBody}>
                                                    <Typography variant="caption" className={classes.fieldLabel} style={{ marginBottom: 10, display: 'block' }}>
                                                        Choose Plan
                                                    </Typography>
                                                    <RadioGroup value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
                                                        <div className={classes.planGrid}>
                                                            {plansList.length === 0 ? (
                                                                <Typography variant="body2" color="textSecondary" style={{ gridColumn: '1 / -1' }}>
                                                                    Loading plans…
                                                                </Typography>
                                                            ) : plansList.map((plan) => {
                                                                const isSelected = selectedPlan === plan.key;
                                                                const isCurrent  = profile?.dq_pro_plan?.plan_key === plan.key;
                                                                return (
                                                                    <Paper
                                                                        key={plan.key}
                                                                        elevation={0}
                                                                        className={`${classes.planCard} ${isSelected ? classes.planCardSelected : ''}`}
                                                                        onClick={() => setSelectedPlan(plan.key)}
                                                                    >
                                                                        <FormControlLabel
                                                                            value={plan.key}
                                                                            control={<Radio color="primary" size="small" />}
                                                                            style={{ margin: 0 }}
                                                                            label={
                                                                                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                                                    <Typography variant="body2" style={{ fontWeight: isSelected ? 700 : 500 }}>
                                                                                        {plan.label || plan.key}
                                                                                    </Typography>
                                                                                    {isCurrent && (
                                                                                        <Chip label="Current" size="small" color="primary"
                                                                                            style={{ height: 16, fontSize: '0.6rem' }} />
                                                                                    )}
                                                                                    {isSelected && !isCurrent && (
                                                                                        <CheckCircleIcon style={{ fontSize: 14, color: '#3f51b5' }} />
                                                                                    )}
                                                                                </Box>
                                                                            }
                                                                        />
                                                                    </Paper>
                                                                );
                                                            })}
                                                        </div>
                                                    </RadioGroup>
                                                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                                        <Button
                                                            onClick={handleActivateSubscription}
                                                            variant="contained"
                                                            color="secondary"
                                                            size="small"
                                                            disabled={activating || !selectedPlan}
                                                            startIcon={activating ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                                        >
                                                            {activating ? 'Activating…' : 'Confirm & Activate'}
                                                        </Button>
                                                        <Button size="small" onClick={() => setActivePanel(null)}>Cancel</Button>
                                                    </Box>
                                                </div>
                                            </Collapse>
                                        </div>
                                    )}

                                    {showOfflinePanel && (
                                        <div className={classes.actionPanel} style={{ marginTop: showActivatePanel ? 8 : 0 }}>
                                            <div className={classes.actionPanelRow} onClick={() => togglePanel('offline')}>
                                                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                    <CreditCardIcon fontSize="small" color="primary" />
                                                    <Box>
                                                        <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                            Mark Invoice Paid (Offline)
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Record a manual or bank-transfer payment
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                {activePanel === 'offline'
                                                    ? <ExpandLessIcon fontSize="small" color="action" />
                                                    : <ExpandMoreIcon fontSize="small" color="action" />}
                                            </div>
                                            <Collapse in={activePanel === 'offline'} timeout={200}>
                                                <div className={classes.actionPanelBody}>
                                                    <TextField
                                                        label="Payment Reference / Note"
                                                        value={offlineNote}
                                                        onChange={(e) => setOfflineNote(e.target.value)}
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        variant="outlined"
                                                        size="small"
                                                        style={{ marginBottom: 14 }}
                                                    />
                                                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                                                        <Button
                                                            onClick={handleMarkInvoicePaidOffline}
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            disabled={markingPaid}
                                                            startIcon={markingPaid ? <CircularProgress size={14} color="inherit" /> : <CreditCardIcon />}
                                                        >
                                                            {markingPaid ? 'Processing…' : 'Mark as Paid'}
                                                        </Button>
                                                        <Button size="small" onClick={() => setActivePanel(null)}>Cancel</Button>
                                                    </Box>
                                                </div>
                                            </Collapse>
                                        </div>
                                    )}

                                    {/* Adjust Credits Panel */}
                                    <div className={classes.actionPanel} style={{ marginTop: (showActivatePanel || showOfflinePanel) ? 8 : 0 }}>
                                        <div className={classes.actionPanelRow} onClick={() => togglePanel('adjust_credits')}>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <CreditCardIcon fontSize="small" color="secondary" />
                                                <Box>
                                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                        Adjust Credits
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Manually add or subtract prepaid credits
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {activePanel === 'adjust_credits'
                                                ? <ExpandLessIcon fontSize="small" color="action" />
                                                : <ExpandMoreIcon fontSize="small" color="action" />}
                                        </div>
                                        <Collapse in={activePanel === 'adjust_credits'} timeout={200}>
                                            <div className={classes.actionPanelBody}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            label="Amount (e.g. 50 or -50)"
                                                            type="number"
                                                            value={adjustCreditsAmount}
                                                            onChange={(e) => setAdjustCreditsAmount(e.target.value)}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={8}>
                                                        <TextField
                                                            label="Reason / Description"
                                                            value={adjustCreditsReason}
                                                            onChange={(e) => setAdjustCreditsReason(e.target.value)}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 14 }}>
                                                    <Button
                                                        onClick={handleAdjustCredits}
                                                        variant="contained"
                                                        color="secondary"
                                                        size="small"
                                                        disabled={adjustingCredits || !adjustCreditsAmount || !adjustCreditsReason}
                                                        startIcon={adjustingCredits ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                                    >
                                                        {adjustingCredits ? 'Adjusting…' : 'Confirm Adjustment'}
                                                    </Button>
                                                    <Button size="small" onClick={() => setActivePanel(null)}>Cancel</Button>
                                                </Box>
                                            </div>
                                        </Collapse>
                                    </div>

                                    {/* Grant DQ Tokens Panel */}
                                    <div className={classes.actionPanel} style={{ marginTop: 8 }}>
                                        <div className={classes.actionPanelRow} onClick={() => togglePanel('grant_dqt')}>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <StarIcon fontSize="small" style={{ color: '#0097b2' }} />
                                                <Box>
                                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                        Grant DQ Tokens
                                                        {dqtInfo ? (
                                                            <span style={{ fontWeight: 400, color: '#6b7280' }}> · balance {Number(dqtInfo.dqt_balance).toLocaleString('en-US')} DQT</span>
                                                        ) : null}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Record an offline payment — converted to DQT at the current rate
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {activePanel === 'grant_dqt'
                                                ? <ExpandLessIcon fontSize="small" color="action" />
                                                : <ExpandMoreIcon fontSize="small" color="action" />}
                                        </div>
                                        <Collapse in={activePanel === 'grant_dqt'} timeout={200}>
                                            <div className={classes.actionPanelBody}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            label="Amount received (USD)"
                                                            type="number"
                                                            value={dqtAmountUsd}
                                                            onChange={(e) => setDqtAmountUsd(e.target.value)}
                                                            inputProps={{ min: 0, step: 0.01 }}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={8}>
                                                        <TextField
                                                            label="Payment reference (bank transfer ID / receipt)"
                                                            value={dqtReference}
                                                            onChange={(e) => setDqtReference(e.target.value)}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                            helperText="Required. Each reference can be granted once."
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            label="Note (optional)"
                                                            value={dqtNote}
                                                            onChange={(e) => setDqtNote(e.target.value)}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 14 }}>
                                                    <Button
                                                        onClick={handleGrantDqt}
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        disabled={grantingDqt || !dqtAmountUsd || !dqtReference.trim()}
                                                        startIcon={grantingDqt ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                                    >
                                                        {grantingDqt ? 'Granting…' : 'Grant DQ Tokens'}
                                                    </Button>
                                                    <Button size="small" onClick={() => setActivePanel(null)}>Cancel</Button>
                                                </Box>

                                                {dqtInfo && dqtInfo.entries.length > 0 && (
                                                    <Box style={{ marginTop: 16 }}>
                                                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                                                            RECENT DQT ACTIVITY
                                                        </Typography>
                                                        {dqtInfo.entries.map((e) => (
                                                            <Box key={e._id} display="flex" justifyContent="space-between" style={{ padding: '4px 0', borderTop: '1px solid #eef0f2', gap: 12 }}>
                                                                <Typography variant="body2" color="textSecondary" noWrap>
                                                                    {e.transaction_type === 'grant'
                                                                        ? `Granted · ref ${e.payment_reference || '—'} · US$${e.amount_usd} at ${e.usd_per_dqt}`
                                                                        : e.transaction_type === 'conversion'
                                                                            ? `Converted into ${e.conversion?.quantity} credits`
                                                                            : e.transaction_type === 'purchase'
                                                                                ? `Bought · ${e.description || 'checkout'} · US$${e.amount_usd}`
                                                                                : (e.description || e.transaction_type)}
                                                                </Typography>
                                                                <Typography variant="body2" style={{ fontWeight: 700, whiteSpace: 'nowrap', color: e.amount_dqt >= 0 ? '#16a34a' : '#374151' }}>
                                                                    {e.amount_dqt >= 0 ? '+' : '−'}{Math.abs(e.amount_dqt).toLocaleString('en-US')} DQT
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}

                                                {dqtOrders.length > 0 && (
                                                    <Box style={{ marginTop: 16 }}>
                                                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                                                            RECENT DQT ORDERS
                                                        </Typography>
                                                        {dqtOrders.map((o) => (
                                                            <Box key={o.order_id} display="flex" justifyContent="space-between" style={{ padding: '4px 0', borderTop: '1px solid #eef0f2', gap: 12 }}>
                                                                <Typography variant="body2" color="textSecondary" noWrap>
                                                                    {o.invoice_no || 'no invoice'} · {o.kind === 'offline_grant' ? 'grant' : o.kind} · {o.label}
                                                                    {' · '}{o.paid_display || o.usd_display}
                                                                </Typography>
                                                                <Typography variant="body2" style={{ fontWeight: 700, whiteSpace: 'nowrap', color: o.status === 'paid' ? '#16a34a' : '#6b7280' }}>
                                                                    {o.status} · {Number(o.dqt_amount || 0).toLocaleString('en-US')} DQT
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </div>
                                        </Collapse>
                                    </div>

                                    {/* Assign Plan Panel — no subscription (R28), optional agreed DQT (R49).
                                        Hidden once a plan is active — see showAssignPlanPanel. */}
                                    {showAssignPlanPanel && (
                                    <div className={classes.actionPanel} style={{ marginTop: 8 }}>
                                        <div className={classes.actionPanelRow} onClick={() => togglePanel('assign_plan')}>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <CheckCircleIcon fontSize="small" style={{ color: '#0097b2' }} />
                                                <Box>
                                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                        Assign Plan (no subscription)
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        After agreeing terms and confirming payment — optionally grant the agreed DQ Tokens
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {activePanel === 'assign_plan'
                                                ? <ExpandLessIcon fontSize="small" color="action" />
                                                : <ExpandMoreIcon fontSize="small" color="action" />}
                                        </div>
                                        <Collapse in={activePanel === 'assign_plan'} timeout={200}>
                                            <div className={classes.actionPanelBody}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            select
                                                            label="Plan"
                                                            value={assignPlanKey}
                                                            onChange={(e) => {
                                                                const { value } = e.target;
                                                                setAssignPlanKey(value);
                                                                if (!assignOrgName) setAssignOrgName(profile?.business_name || '');
                                                                // The org's country is saved as its ISO code; an older profile may hold a name.
                                                                if (!assignCountry) setAssignCountry(findCountry(profile?.country)?.code || '');
                                                            }}
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                            helperText="Activates the plan with no Stripe subscription. A workspace already on another plan is refused — plan change is still open."
                                                        >
                                                            {plansList.map((p) => (
                                                                <MenuItem key={p.key} value={p.key}>{p.label || p.key}</MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FormControlLabel
                                                            control={<Checkbox checked={assignWithGrant} onChange={(e) => setAssignWithGrant(e.target.checked)} color="primary" />}
                                                            label={plansList.find((p) => p.key === assignPlanKey)?.channel === 'direct' ? 'Also add credits from the payment' : 'Also grant DQ Tokens'}
                                                        />
                                                    </Grid>
                                                    {plansList.find((p) => p.key === assignPlanKey)?.channel === 'direct' && (
                                                        <>
                                                            <Grid item xs={12} sm={6}>
                                                                <RadioGroup
                                                                    row
                                                                    value={assignAccountType}
                                                                    onChange={(e) => { const { value } = e.target; setAssignAccountType(value); }}
                                                                >
                                                                    <FormControlLabel value="school" control={<Radio color="primary" size="small" />} label="A single school" />
                                                                    <FormControlLabel value="school_network" control={<Radio color="primary" size="small" />} label="A school network" />
                                                                </RadioGroup>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    A direct plan makes this workspace a school or a school network. Used only the first time — an existing organisation is kept.
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={3}>
                                                                <TextField
                                                                    label={assignAccountType === 'school_network' ? 'School network name' : 'School name'}
                                                                    value={assignOrgName}
                                                                    onChange={(e) => { const { value } = e.target; setAssignOrgName(value.slice(0, 200)); }}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={3}>
                                                                <TextField
                                                                    select
                                                                    label="Country"
                                                                    value={assignCountry}
                                                                    onChange={(e) => { const { value } = e.target; setAssignCountry(value); }}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    size="small"
                                                                >
                                                                    {getCountries().map((c) => <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>)}
                                                                </TextField>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {assignWithGrant && (
                                                        <>
                                                            <Grid item xs={12} sm={5}>
                                                                <TextField
                                                                    select
                                                                    label="How much"
                                                                    value={assignGrantMode}
                                                                    onChange={(e) => setAssignGrantMode(e.target.value)}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    size="small"
                                                                    helperText={assignGrantMode === 'plan_price' ? planPriceHint
                                                                        : (plansList.find((p) => p.key === assignPlanKey)?.channel === 'direct' ? 'Converted into credits at the plan\'s credit price' : 'Converted to DQT at the current rate')}
                                                                >
                                                                    <MenuItem value="plan_price">The plan price</MenuItem>
                                                                    <MenuItem value="custom">A different amount</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            {assignGrantMode === 'custom' && (
                                                                <Grid item xs={12} sm={3}>
                                                                    <TextField
                                                                        label="Amount received (USD)"
                                                                        type="number"
                                                                        value={assignUsd}
                                                                        onChange={(e) => setAssignUsd(e.target.value)}
                                                                        inputProps={{ min: 0, step: 0.01 }}
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        size="small"
                                                                    />
                                                                </Grid>
                                                            )}
                                                            <Grid item xs={12} sm={assignGrantMode === 'custom' ? 4 : 7}>
                                                                <TextField
                                                                    label="Payment reference (bank transfer ID / receipt)"
                                                                    value={assignRef}
                                                                    onChange={(e) => setAssignRef(e.target.value)}
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    size="small"
                                                                    helperText="Required with a grant. Each reference can be granted once."
                                                                />
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                                <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 14 }}>
                                                    <Button
                                                        onClick={handleAssignPlan}
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        disabled={
                                                            assigningPlan || !assignPlanKey
                                                            || (assignWithGrant && !assignRef.trim())
                                                            || (assignWithGrant && assignGrantMode === 'custom' && !assignUsd)
                                                            || (assignWithGrant && assignGrantMode === 'plan_price' && !!planPreview && planPreview.can_grant_plan_price === false)
                                                        }
                                                        startIcon={assigningPlan ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                                                    >
                                                        {assigningPlan ? 'Assigning…' : 'Assign Plan'}
                                                    </Button>
                                                    <Button size="small" onClick={() => setActivePanel(null)}>Cancel</Button>
                                                </Box>
                                            </div>
                                        </Collapse>
                                    </div>
                                    )}

                                    {/* Product Access Control Panel */}
                                    <div className={classes.actionPanel} style={{ marginTop: 8 }}>
                                        <div className={classes.actionPanelRow} onClick={() => setOpenPACDialog(true)}>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <AssignmentIcon fontSize="small" color="primary" />
                                                <Box>
                                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                        Manage Product Access
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Enable or disable specific products for this Insider
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <ExpandMoreIcon fontSize="small" color="action" />
                                        </div>
                                    </div>

                                    {/* Features + Branding Panel */}
                                    <div className={classes.actionPanel} style={{ marginTop: 8 }}>
                                        <div className={classes.actionPanelRow} onClick={() => setOpenFeaturesDialog(true)}>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <AssignmentIcon fontSize="small" color="primary" />
                                                <Box>
                                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                        Manage Features
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Override platform features and white-label branding
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <ExpandMoreIcon fontSize="small" color="action" />
                                        </div>
                                    </div>

                                    {/* Template Creator Panel */}
                                    <div className={classes.actionPanel} style={{ marginTop: 8 }}>
                                        <div className={classes.actionPanelRow} style={{ cursor: 'default' }}>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <StarIcon fontSize="small" color="secondary" />
                                                <Box>
                                                    <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.4 }}>
                                                        Template Creator
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Lets this insider author free DQ showcase/template learning programs in dq-seal-client
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Switch
                                                checked={templateCreator}
                                                onChange={handleToggleTemplateCreator}
                                                disabled={savingTemplateCreator || !profile?.dq_insider_id}
                                                color="primary"
                                            />
                                        </div>
                                    </div>

                                </div>
                            </Grid>
                        )}

                    </Grid>
                ) : (
                    <Box p={4} textAlign="center">
                        <AssignmentIcon color="disabled" style={{ fontSize: 48, marginBottom: 16 }} />
                        <Typography color="textSecondary">Profile data could not be loaded.</Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions style={{ padding: '12px 24px' }}>
                <Button onClick={onClose} color="primary" variant="contained" disableElevation>
                    Close
                </Button>
            </DialogActions>

            <InsiderProductAccessDialog
                open={openPACDialog}
                onClose={() => setOpenPACDialog(false)}
                onClosed={notifyRefresh}
                dq_insider_id={profile?.dq_insider_id}
            />

            <InsiderFeaturesDialog
                open={openFeaturesDialog}
                onClose={() => setOpenFeaturesDialog(false)}
                onClosed={notifyRefresh}
                dq_insider_id={profile?.dq_insider_id}
                workspaceName={profile?.business_name
                    || [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim()
                    || profile?.email
                    || ''}
            />
        </Dialog>
    );
};

export default PBDQDetailsDialog;
